import { CommonModule } from '@angular/common';
import { Component, EnvironmentInjector, Inject, Input } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { Modal, ModalOptions } from '@indice/ng-components';
import { catchError, EMPTY, tap } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { IAsideModalResponse } from 'src/app/features/property-details/aside-content/aside-content.component';
import {
  AcceptTnCRequest,
  HttpValidationProblemDetails,
  IAcceptTnCRequest,
  PortalApiService,
  PropertyDetails
} from 'src/app/services/portal-api.service';
import { ToasterService } from 'src/app/services/toaster.service';
import { WorkflowApiService } from 'src/app/services/workflow.service';
import * as environment from '../../../core/appSettings';
import { RequestsUnauthorizedComponent, UnauthorizedRequestsTranslationSuffix } from '../requests-unauthorized/requests-unauthorized.component';
import {
  IWalkthroughDate,
  RequestWalkthroughSelectDateComponent,
} from './request-walkthrough-select-date/request-walkthrough-select-date.component';
import { RequestWalkthroughSuccessComponent } from './request-walkthrough-success/request-walkthrough-success.component';
import { RequestWalkthroughVerifyPhoneComponent } from './request-walkthrough-verify-phone/request-walkthrough-verify-phone.component';

@Component({
  selector: 'app-request-walkthrough-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    RequestWalkthroughSelectDateComponent,
    RequestWalkthroughVerifyPhoneComponent,
    RequestWalkthroughSuccessComponent,
    RequestsUnauthorizedComponent,
  ],
  templateUrl: './request-walkthrough-modal.component.html',
})
export class RequestWalkthroughModalComponent {
  @Input()
  property: PropertyDetails | undefined;

  @Input()
  isLoggedIn: boolean = false;

  form = new UntypedFormGroup({
    message: new UntypedFormControl(''),
  });

  public unauthorizedSuffix: UnauthorizedRequestsTranslationSuffix = UnauthorizedRequestsTranslationSuffix.REQUEST_WALKTHROUGH;
  public RequestWalkthroughSteps = RequestWalkthroughSteps;
  public currentStep: RequestWalkthroughSteps =
    RequestWalkthroughSteps.SelectDate;
  public selectedDate: IWalkthroughDate | undefined;

  constructor(
    public modalRef: Modal,
    @Inject(ModalOptions) private _options: ModalOptions,
    private _authService: AuthService,
    private _api: PortalApiService,
    private _workflowApi: WorkflowApiService,
    @Inject(ToasterService) private _toastService: ToasterService,
    @Inject(EnvironmentInjector) private envInjector: EnvironmentInjector,
  ) {}

  ngOnInit(): void {
    this.property = this._options?.initialState?.[
      'property'
    ] as PropertyDetails;
    this.isLoggedIn = this._options?.initialState?.['isLoggedIn'] as boolean;
  }

  close = () => this.modalRef.hide({ name: this.form.get('message')?.value });

  public onDateSelected(date: IWalkthroughDate) {
    this.selectedDate = date;
    this._api
      .acceptTerms(
        new AcceptTnCRequest({
          propertyId: this.property?.id,
          content: '',
        } as IAcceptTnCRequest),
      )
      .subscribe(() => {
        this.currentStep = RequestWalkthroughSteps.VerifyPhone;
      });
  }

  public onPhoneVerified() {
    this._workflowApi
      .initiateWalkthrough({
        propertyId: this.property?.id ?? '',
        walkthroughDetails: {
          proposedTimeSlotFrom: this.getFullDateTime(
            this.selectedDate?.date,
            this.selectedDate?.fromTime,
          ),
          proposedTimeSlotTo: this.getFullDateTime(
            this.selectedDate?.date,
            this.selectedDate?.toTime,
          ),
        },
      })
      .pipe(
        catchError((error: any) => {
          let errorBody = undefined;
          if (error instanceof HttpValidationProblemDetails) {
            if (error.errors) {
              errorBody = Object.entries(error.errors)
                .map((m) => m[1])
                .flat()
                .join(', ');
            }
          }
          let rs: IAsideModalResponse = {
            success: false,
            errorDesc: errorBody,
          };
          this.modalRef.hide(rs);

          return EMPTY;
        }),
        // tap(() => {
        //   let rs: IAsideModalResponse = {
        //     success: true,
        //   };
        //   this.modalRef.hide(rs);
        // }),
      )
      .subscribe(() => {
        this.currentStep = RequestWalkthroughSteps.Success;
      });
  }

  public closeModal() {
    this.modalRef.hide({
      name: this.form.get('message')?.value,
      success: true,
    });
  }

  login() {
    this._authService.signinRedirect(`/properties/${this.property?.code}`);
  }

  signUp() {
    var authBasePath = environment.getAppSettings(this.envInjector).auth_settings.authority;
    var portalAuthRedirect =
      environment.getAppSettings(this.envInjector).auth_settings.post_logout_redirect_uri;
    window.location.href = `${authBasePath}/on-boarding/get-started?returnurl=${portalAuthRedirect}/properties/${this.property?.id}`;
    ///home?action=walkthrough&propertyId=${this.property?.id}&date=${this.selectedDate?.date}&from=${this.selectedDate?.fromTime}&to=${this.selectedDate?.toTime}
  }

  private getFullDateTime(baseDate?: Date, time?: string): string {
    if (!baseDate || !time) {
      return new Date('1970-01-01T00:00:00.000Z').toISOString(); // Default date
    }

    const [hours, minutes] = time.split(':').map(Number);
    const date = new Date(baseDate); // clone the date to avoid mutating the original
    date.setHours(hours, minutes, 0, 0); // set hours and minutes

    return date.toISOString();
  }
}

export enum RequestWalkthroughSteps {
  SelectDate = 0,
  VerifyPhone = 1,
  Success = 2,
}
